/**
 * A locally unique id, in every context the app is served from.
 *
 * `crypto.randomUUID()` is defined only in a **secure context** — https, or
 * localhost. A server reached over plain http on a LAN address or bare IP is
 * not one, and there the property is simply absent: calling it throws a
 * TypeError rather than degrading.
 *
 * That mattered far more than it looks. `useScenarioStore` builds its first
 * scenario at module scope, so the throw landed while the bundle was still
 * evaluating: React never mounted, the login screen never rendered, and the
 * app never even asked the API for a session. The symptom was a blank page
 * with a working title and favicon, from a server whose logs showed every
 * asset served with a 200 — and it only ever appeared once deployed, because
 * `localhost` in development is a secure context.
 *
 * `crypto.getRandomValues()` carries no such restriction, so the fallback is
 * real randomness rather than `Math.random()`. These ids identify scenarios
 * and shortlist entries inside one browser's localStorage; they are not
 * secrets and nothing authenticates with them.
 */
export function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // Version 4, variant 1 — the two fields the format actually pins down.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
